import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import pb from '../pocketbase';
import 'leaflet/dist/leaflet.css';
import './TrustMap.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaCheckCircle, FaMousePointer, FaExpand, FaTimes } from 'react-icons/fa';

// Component to handle map view updates
const MapUpdater = ({ center, userLocation }) => {
    const map = useMap();
    useEffect(() => {
        if (userLocation) {
            const bounds = L.latLngBounds([center, [userLocation.lat, userLocation.lon]]);
            map.fitBounds(bounds, { padding: [50, 50], animate: true });
        }
    }, [userLocation, map, center]);
    return null;
};

// Fix for default marker icons in Leaflet + React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom Blue Icon for RoulezPropre
const roulezIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const TrustMap = () => {
    const [interventions, setInterventions] = useState([]);
    const [isInteractive, setIsInteractive] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const center = [49.0067, 1.9100]; // Meulan-en-Yvelines center

    useEffect(() => {
        const handleMapUpdate = (e) => {
            setUserLocation(e.detail);
            setIsInteractive(true);
        };
        window.addEventListener('map-update', handleMapUpdate);
        return () => window.removeEventListener('map-update', handleMapUpdate);
    }, []);

    useEffect(() => {
        const fetchInterventions = async () => {
            try {
                const records = await pb.collection('rendez_vous').getFullList({
                    filter: 'statut = "termine" && lat != null',
                    sort: '-date_heure'
                });
                setInterventions(records);
            } catch (err) {
                console.error("Map Load Error", err);
            }
        };

        fetchInterventions();
    }, []);

    return (
        <section className="trust-map-section-premium">
            <div className="container text-center">
                <motion.div
                    className="section-header text-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <motion.span
                        className="section-label"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <FaMapMarkerAlt style={{ marginRight: '8px' }} />
                        Zone d'Intervention
                    </motion.span>
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        📍 Nos <span className="text-gradient-blue">Zones d'Intervention</span> locales
                    </motion.h2>
                    <motion.p
                        className="section-subtitle-text"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                    >
                        Consultez la carte de nos dernières interventions pour découvrir nos secteurs de nettoyage.
                    </motion.p>
                </motion.div>

                <motion.div
                    className="map-container-premium"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                >
                    <div className="map-wrapper-premium">
                        <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                             <div className="service-area-badge" style={{ top: 'auto', right: 'auto', bottom: '20px', left: '20px' }}>
                                Secteur : 20km autour de Meulan
                            </div>
                            <MapContainer
                                center={center}
                                zoom={11}
                                scrollWheelZoom={false}
                                className="leaflet-container-custom"
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <MapUpdater center={center} userLocation={userLocation} />

                                {/* 20km Radius Circle */}
                                <Circle
                                    center={center}
                                    radius={20000}
                                    pathOptions={{
                                        fillColor: '#2563eb',
                                        fillOpacity: 0.1,
                                        color: '#2563eb',
                                        weight: 2,
                                        dashArray: '10, 10'
                                    }}
                                />

                                {interventions.map((apt) => (
                                    <Marker
                                        key={apt.id}
                                        position={[apt.lat, apt.lon]}
                                        icon={roulezIcon}
                                    >
                                        <Popup>
                                            <div className="map-popup-premium">
                                                <div className="popup-icon"><FaCheckCircle /></div>
                                                <strong>{apt.prestation}</strong>
                                                <p>Terminé</p>
                                            </div>
                                        </Popup>
                                    </Marker>
                                ))}

                                {userLocation && (
                                    <>
                                        <Marker position={[userLocation.lat, userLocation.lon]}>
                                            <Popup>Votre adresse</Popup>
                                        </Marker>
                                        <Polyline
                                            positions={[center, [userLocation.lat, userLocation.lon]]}
                                            pathOptions={{
                                                color: userLocation.distance > 20 ? '#ef4444' : '#22c55e',
                                                weight: 4,
                                                dashArray: '10, 10',
                                                opacity: 0.8
                                            }}
                                        />
                                    </>
                                )}
                            </MapContainer>

                            {/* Fullscreen Trigger */}
                            <motion.button
                                className="btn-fullscreen-toggle"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setIsFullscreen(true)}
                            >
                                <FaExpand />
                            </motion.button>
                        </div>


                    </div>
                    <div className="map-glow"></div>
                </motion.div>

                {/* MODAL FULLSCREEN (MODE FOCUS) */}
                <AnimatePresence>
                    {isFullscreen && (
                        <motion.div
                            className="map-modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsFullscreen(false)}
                        >
                            <motion.div
                                className="map-modal-content"
                                initial={{ scale: 0.8, opacity: 0, y: 50 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.8, opacity: 0, y: 50 }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="map-modal-header">
                                    <div className="modal-badge-premium">
                                        <FaMapMarkerAlt />
                                        <span>Meulan & Alentours (20km)</span>
                                    </div>
                                    <button
                                        className="btn-close-map-modal"
                                        onClick={() => setIsFullscreen(false)}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>

                                <MapContainer
                                    center={center}
                                    zoom={12}
                                    scrollWheelZoom={true}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <MapUpdater center={center} userLocation={userLocation} />

                                    <Circle
                                        center={center}
                                        radius={20000}
                                        pathOptions={{
                                            fillColor: '#2563eb',
                                            fillOpacity: 0.1,
                                            color: '#2563eb',
                                            weight: 2,
                                            dashArray: '10, 10'
                                        }}
                                    />

                                    {interventions.map((apt) => (
                                        <Marker
                                            key={apt.id}
                                            position={[apt.lat, apt.lon]}
                                            icon={roulezIcon}
                                        >
                                            <Popup>
                                                <div className="map-popup-premium">
                                                    <div className="popup-icon"><FaCheckCircle /></div>
                                                    <strong>{apt.prestation}</strong>
                                                    <p>Terminé</p>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}

                                    {userLocation && (
                                        <>
                                            <Marker position={[userLocation.lat, userLocation.lon]}>
                                                <Popup>Votre adresse</Popup>
                                            </Marker>
                                            <Polyline
                                                positions={[center, [userLocation.lat, userLocation.lon]]}
                                                pathOptions={{
                                                    color: userLocation.distance > 20 ? '#ef4444' : '#22c55e',
                                                    weight: 4,
                                                    dashArray: '10, 10',
                                                    opacity: 0.8
                                                }}
                                            />
                                        </>
                                    )}
                                </MapContainer>

                                <div className="map-modal-footer">
                                    <div className="modal-info-card">
                                        <span className="pricing-title">
                                            Déplacement <span className="text-highlight">OFFERT</span> (20km)
                                        </span>
                                        <span className="pricing-detail">
                                            *0.70€/km au-delà de Meulan-en-Yvelines
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pricing Info Box */}
                <motion.div
                    className="pricing-info-box"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 }}
                >
                    <span className="pricing-title">
                        Déplacement <span className="text-highlight">OFFERT</span> dans un périmètre de 20km*
                    </span>
                    <span className="pricing-detail">
                        *Possibilité d'intervenir sur des communes hors périmètre avec supplément (0.70 centimes par kilomètre.)
                    </span>
                </motion.div>
            </div>
        </section>
    );
};

export default TrustMap;
